import { ReactNode } from 'react';
import Image from 'next/image';

import {
  Box,
  BoxProps,
  Flex,
  Grid,
  Heading,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Tag,
  Text,
  useToken,
} from '@chakra-ui/react';
import { HistoryIcon } from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { useCatalogItems } from '~/features/catalog/hooks/use-catalog-items';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { useCourseLink } from '~/features/program/hooks/use-course-link';

export type PopularCoursesSectionProps = {
  title: string;
  blurb: string;
  programKeys: string[];
};

export function PopularCoursesSection({ title, blurb, programKeys }: PopularCoursesSectionProps) {
  const [containerWidth] = useToken('sizes', ['content-max-width']);
  const { catalogItems, catalogItemsIsLoading } = useCatalogItems(programKeys);

  return (
    <Box
      as='section'
      bgColor='white'
      padding={{
        base: '40px 0px',
        xl: '80px 0px',
      }}
    >
      <ContentContainer>
        <Box mb={10} textAlign='center'>
          <Heading
            as='h2'
            mb={4}
            textAlign='center'
            size={{
              base: 'marketing-3xl',
              xl: 'marketing-5xl',
            }}
          >
            {title}
          </Heading>
          <Text>{blurb}</Text>
        </Box>
      </ContentContainer>
      <Flex
        overflow='auto'
        paddingEnd={{
          md: '16px',
        }}
        paddingStart={{
          xl: `calc((100% - ${containerWidth})/2)`,
        }}
        sx={{
          scrollBarWidth: 'thin',
          '&::before,&::after': {
            content: "''",
            display: 'block',
            width: {
              base: '16px',
              md: '24px',
              lg: '48px',
              xl: 'unset',
            },
            flexShrink: 0,
          },
        }}
      >
        <Grid
          gap={4}
          gridAutoFlow='column'
          gridAutoRows='full'
          gridAutoColumns={{
            base: '210px',
            xl: '268px',
          }}
        >
          {catalogItemsIsLoading && (
            <>
              <SkeletonProgramCard />
              <SkeletonProgramCard />
              <SkeletonProgramCard />
              <SkeletonProgramCard />
            </>
          )}
          {!catalogItemsIsLoading &&
            catalogItems?.map((catalogItem) => <ProgramCard {...catalogItem} key={catalogItem.key} />)}
        </Grid>
      </Flex>
    </Box>
  );
}

function ProgramCard({ image, title, summary, slug, rawDurationDisplay, difficultyLevel }: CatalogItem) {
  const courseLink = useCourseLink({ courseSlug: slug });
  if (!image || !title || !slug) return null;
  return (
    <LinkBox
      borderRadius='md'
      display='flex'
      role='group'
      transition='all 0.3s ease-in-out'
      _hover={{
        boxShadow: 'lg',
      }}
    >
      <ProgramCardLayout
        contentSlot={
          <Flex flex='1' flexDir='column' gap={6}>
            <Box>
              <Heading
                as={LinkOverlay}
                display='block'
                href={courseLink}
                mb={2}
                size='marketing-lg'
                onClick={() =>
                  trackEvent({
                    type: 'click',
                    name: 'skills_page_cta_clicked',
                    label: 'popular_courses',
                    value: title,
                  })
                }
              >
                {title}
              </Heading>
              <Text fontSize='sm' noOfLines={5}>
                {summary}
              </Text>
            </Box>
            <Flex
              gap={2}
              justifyContent='space-between'
              marginTop='auto'
              alignItems={{
                base: 'flex-start',
                xl: 'center',
              }}
              flexDir={{
                base: 'column',
                xl: 'row',
              }}
            >
              <Box>
                <Flex alignItems='center' gap={1} hidden={!rawDurationDisplay}>
                  <HistoryIcon height='24px' width='24px' />
                  <Text size='caption'>{rawDurationDisplay}</Text>
                </Flex>
              </Box>
              <Tag hidden={!difficultyLevel} textTransform='capitalize'>
                {difficultyLevel}
              </Tag>
            </Flex>
          </Flex>
        }
        imageSlot={
          <Box
            position='relative'
            width='100%'
            height={{
              base: '132px',
              xl: '168px',
            }}
          >
            <Image
              alt=''
              sizes='268px'
              src={image}
              style={{
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
              fill
            />
            <Box
              bgGradient='linear(to-b, transparent, white, transparent)'
              bottom={0}
              height='120%'
              mixBlendMode='soft-light'
              opacity={0.3}
              pointerEvents='none'
              position='absolute'
              right={0}
              transform='translateX(50%) translateY(100%) rotate(-20deg)'
              transition='transform 0.3s ease-in-out'
              width='200%'
              _groupHover={{
                transform: 'translateX(10%) translateY(-10%) rotate(-20deg)',
              }}
            />
          </Box>
        }
      />
    </LinkBox>
  );
}

function SkeletonProgramCard() {
  return (
    <ProgramCardLayout
      as='div'
      contentSlot={
        <>
          <Skeleton height='30px' maxWidth='194px' mb={2} width='full' />
          <Skeleton height='92px' mb={2} width='full' />
          <Box>
            <Flex
              gap={2}
              justifyContent='space-between'
              alignItems={{
                base: 'flex-start',
                xl: 'center',
              }}
              flexDir={{
                base: 'column',
                xl: 'row',
              }}
            >
              <Flex gap={1}>
                <Skeleton height='24px' width='24px' />
                <Skeleton height='24px' width='71px' />
              </Flex>
              <Skeleton height='16px' width='69px' />
            </Flex>
          </Box>
        </>
      }
      imageSlot={
        <Skeleton
          width='full'
          height={{
            base: '132px',
            xl: '168px',
          }}
        />
      }
    />
  );
}

function ProgramCardLayout({
  imageSlot = null,
  contentSlot = null,
  footerSlot = null,
  ...rest
}: { imageSlot?: ReactNode; contentSlot?: ReactNode; footerSlot?: ReactNode } & BoxProps) {
  return (
    <Card as='article' display='flex' flexDir='column' overflow='hidden' {...rest}>
      {imageSlot}
      <Flex
        flex='1'
        flexDir='column'
        p={{
          base: 4,
          xl: 6,
        }}
      >
        {contentSlot}
      </Flex>
    </Card>
  );
}
